<template>
  <div :class="['email-component', 'html-component', { selected }]" @click="$emit('click')">
    <div v-if="!hasHtml" class="html-empty-state">
      <i class="tim-icons icon-html5"></i>
      <span>Custom HTML block. Select it and use Edit HTML in the sidebar to add your code.</span>
    </div>
    <div v-else class="html-frame-wrap" :style="paddingStyles">
      <!--
        sandbox="allow-same-origin" and nothing else: scripts cannot execute
        because allow-scripts is absent, while same-origin access lets the
        parent read contentDocument for the height measurement. NEVER add
        allow-scripts: combined with allow-same-origin the framed content
        could remove its own sandbox.
      -->
      <iframe
        ref="frame"
        class="html-frame"
        sandbox="allow-same-origin"
        :srcdoc="srcdoc"
        :style="{ height: frameHeight + 'px' }"
        title="Custom HTML preview"
        @load="measure"
      ></iframe>
      <!-- Catches clicks so selecting the block works; the iframe would swallow them. -->
      <div class="html-frame-overlay"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { fluidizeFixedWidths } from '@/util/emailHtml'

const props = defineProps<{
  componentData: {
    html?: string
    hoistedStyles?: string
    styles?: Record<string, string>
  }
  selected?: boolean
}>()

defineEmits(['click'])

const frame = ref<HTMLIFrameElement | null>(null)
const frameHeight = ref(40)

const hasHtml = computed(() => Boolean((props.componentData.html || '').trim()))

const srcdoc = computed(() => {
  const styles = props.componentData.hoistedStyles || ''
  // Same rewrite the renderer applies on send, so the preview matches the email.
  const html = fluidizeFixedWidths(props.componentData.html || '')
  return `<!doctype html><html><head><style>body{margin:0;padding:0}</style><style>${styles}</style></head><body>${html}</body></html>`
})

const paddingStyles = computed(() => {
  const s = props.componentData.styles || {}
  return {
    padding: `${s.paddingTop || '0'}px ${s.paddingRight || '0'}px ${s.paddingBottom || '0'}px ${s.paddingLeft || '0'}px`,
  }
})

function measure() {
  const el = frame.value
  if (!el || !el.contentDocument || !el.contentDocument.body) return
  frameHeight.value = Math.max(40, el.contentDocument.body.scrollHeight)
}

// srcdoc changes re-trigger load, but some browsers skip the event when only
// the string identity changes; measure on next frame as a fallback.
watch(srcdoc, () => {
  requestAnimationFrame(() => measure())
})
</script>

<style scoped>
.html-component {
  position: relative;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  border-radius: 4px;
  cursor: pointer;
}

.html-component:hover {
  border-color: rgba(225, 78, 202, 0.3);
  background: rgba(225, 78, 202, 0.05);
}

.html-component.selected {
  border-color: #e14eca;
  box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.1);
  background: rgba(225, 78, 202, 0.05);
}

.html-empty-state {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px 16px;
  color: rgba(125, 125, 125, 0.8);
  font-size: 0.8125rem;
}

.html-empty-state i {
  font-size: 1.25rem;
}

.html-frame-wrap {
  position: relative;
}

.html-frame {
  display: block;
  width: 100%;
  border: none;
  background: transparent;
}

.html-frame-overlay {
  position: absolute;
  inset: 0;
}
</style>
